import React from 'react'
import {
  Dialog,
  DialogContent,
  Typography,
  ClearIcon,
  Grid,
  styledComponent,
  ImageList,
  ImageListItem,
  Image,
  ExpandMoreIcon,
  useTheme,
  useMediaQuery,
  FiberManualRecordIcon
} from '@hermes/web-components'
import GalleryLightbox from '../GalleryLightbox'

type GalleryModalProps = {
  isOpen: boolean
  onClose: () => void
  images: { id: number; image: string }[]
  selectedImage?: number
  setSelectedImage: (id?: number) => void
}

const StyledDialog = styledComponent(Dialog)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      margin: 0,
      width: '100%',
      height: '100%',
      borderRadius: 0
    },
    '& .MuiDialogContent-root': {
      overflow: 'none',
      width: '100%'
    }
  }
}))

const GalleryModal = ({ isOpen, onClose, images, selectedImage, setSelectedImage }: GalleryModalProps) => {
  const theme = useTheme()
  const onlySmallScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [showLightbox, setShowLightbox] = React.useState(false)
  const toggleModal = () => setShowLightbox((prev: boolean) => !prev)

  const nextImage = () => {
    if (selectedImage !== undefined) {
      const imageCount = images.length
      if (imageCount - 1 > selectedImage) {
        setSelectedImage(selectedImage + 1)
      } else {
        setSelectedImage(0)
      }
    }
  }

  const prevImage = () => {
    if (selectedImage !== undefined) {
      const imageCount = images.length
      if (selectedImage === 0) {
        setSelectedImage(imageCount - 1)
      } else {
        setSelectedImage(selectedImage - 1)
      }
    }
  }

  const mainImage = images.find((image) => image.id === selectedImage) || { id: '', image: '' }
  return (
    <StyledDialog open={isOpen} onClose={onClose} maxWidth="md">
      <DialogContent sx={{ px: 4, py: 3, width: '100%', maxWidth: 896, maxHeight: 620 }}>
        <Grid container justifyContent="space-between" wrap="nowrap">
          <Typography variant="h2">Gallery</Typography>
          <ClearIcon onClick={onClose} sx={{ cursor: 'pointer' }} />
        </Grid>
        <Grid container justifyContent="space-between" wrap="nowrap" spacing={4}>
          <Grid item display="flex" justifyContent="center" width="100%" sx={{ mt: 3 }}>
            <Image
              lazy
              src={mainImage.image}
              height={onlySmallScreen ? '100%' : '480px'}
              width="100%"
              alt={mainImage.image}
            />
          </Grid>
          {!onlySmallScreen && (
            <Grid
              item
              sx={{
                mt: 3,
                justifyContent: 'space-between',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} onClick={prevImage} />
              <ImageList cols={1} rowHeight={80} sx={{ maxHeight: 400, paddingX: '30px' }}>
                {images.map((image) => (
                  <ImageListItem key={`practice-${image.id}`}>
                    <Image
                      lazy
                      onClick={() => setSelectedImage(image.id)}
                      src={image.image}
                      height="80px"
                      width="80px"
                      alt={image.image}
                      sx={{
                        border: selectedImage === image.id ? 'transparent solid 2px' : 0,
                        borderColor: 'primary.main'
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
              <ExpandMoreIcon onClick={nextImage} />
            </Grid>
          )}
        </Grid>
        {onlySmallScreen && (
          <Grid container justifyContent="center" alignItems="center" mt={4}>
            <ExpandMoreIcon
              sx={{ transform: 'rotate(90deg)', color: '#80858D', width: '15px', height: '15px', mr: 2 }}
              onClick={prevImage}
            />
            {images.map((image) => {
              const isSelected = image.id === selectedImage
              return (
                <FiberManualRecordIcon
                  key={image.id}
                  onClick={() => setSelectedImage(image.id)}
                  sx={{
                    mr: 0.25,
                    ml: 0.25,
                    width: isSelected ? '10px' : '8px',
                    height: isSelected ? '10px' : '8px',
                    color: isSelected ? 'primary.main' : '#ABADB3'
                  }}
                />
              )
            })}
            <ExpandMoreIcon
              onClick={nextImage}
              sx={{ transform: 'rotate(270deg)', color: '#80858D', width: '15px', height: '15px', ml: 2 }}
            />
          </Grid>
        )}
      </DialogContent>
      <GalleryLightbox isOpen={showLightbox} onClose={toggleModal} image={mainImage.image} />
    </StyledDialog>
  )
}

export default GalleryModal
